import { useState, useRef } from "react";
import { cn, transliterateToKazakh, transliterateToLatin, transliterateRussianToKazakh, transliterateEnglishToKazakhLatin } from "@/lib/utils";
import { Copy, Check } from "lucide-react";
import { VirtualKeyboard } from "./VirtualKeyboard";

type Direction = "latinToCyrillic" | "cyrillicToLatin" | "russianToKazakh" | "englishToKazakhLatin";

export function Transliterator() {
  const [inputText, setInputText] = useState("");
  const [transliteratedText, setTransliteratedText] = useState("");
  const [copied, setCopied] = useState(false);
  const [direction, setDirection] = useState<Direction>("latinToCyrillic");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setInputText(text);

    if (direction === "latinToCyrillic") {
      setTransliteratedText(transliterateToKazakh(text));
    } else if (direction === "cyrillicToLatin") {
      setTransliteratedText(transliterateToLatin(text));
    } else if (direction === "russianToKazakh") {
      setTransliteratedText(transliterateRussianToKazakh(text));
    } else {
      setTransliteratedText(transliterateEnglishToKazakhLatin(text));
    }
  };

  const clearText = () => {
    setInputText("");
    setTransliteratedText("");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transliteratedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleVirtualKeyClick = (key: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Handle backspace
    if (key === "⌫") {
      if (start === end && start > 0) {
        // Delete character before cursor
        const newText = inputText.slice(0, start - 1) + inputText.slice(end);
        const newCursorPos = start - 1;
        setInputText(newText);

        // Update transliteration
        if (direction === "latinToCyrillic") {
          setTransliteratedText(transliterateToKazakh(newText));
        } else if (direction === "cyrillicToLatin") {
          setTransliteratedText(transliterateToLatin(newText));
        } else if (direction === "russianToKazakh") {
          setTransliteratedText(transliterateRussianToKazakh(newText));
        } else {
          setTransliteratedText(transliterateEnglishToKazakhLatin(newText));
        }

        // Restore cursor position
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      } else if (start !== end) {
        // Delete selected text
        const newText = inputText.slice(0, start) + inputText.slice(end);
        setInputText(newText);

        // Update transliteration
        if (direction === "latinToCyrillic") {
          setTransliteratedText(transliterateToKazakh(newText));
        } else if (direction === "cyrillicToLatin") {
          setTransliteratedText(transliterateToLatin(newText));
        } else if (direction === "russianToKazakh") {
          setTransliteratedText(transliterateRussianToKazakh(newText));
        } else {
          setTransliteratedText(transliterateEnglishToKazakhLatin(newText));
        }

        // Restore cursor position
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start);
        }, 0);
      }
      return;
    }

    // Insert character at cursor position
    const newText = inputText.slice(0, start) + key + inputText.slice(end);
    const newCursorPos = start + key.length;

    setInputText(newText);

    // Update transliteration
    if (direction === "latinToCyrillic") {
      setTransliteratedText(transliterateToKazakh(newText));
    } else if (direction === "cyrillicToLatin") {
      setTransliteratedText(transliterateToLatin(newText));
    } else if (direction === "russianToKazakh") {
      setTransliteratedText(transliterateRussianToKazakh(newText));
    } else {
      setTransliteratedText(transliterateEnglishToKazakhLatin(newText));
    }

    // Restore cursor position and focus
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const isLatinToCyrillic = direction === "latinToCyrillic";
  const isCyrillicToLatin = direction === "cyrillicToLatin";
  const isRussianToKazakh = direction === "russianToKazakh";
  const isEnglishToKazakhLatin = direction === "englishToKazakhLatin";

  const inputLabel = isLatinToCyrillic
    ? "Latin Text (Input)"
    : isCyrillicToLatin
    ? "Kazakh Cyrillic (Input)"
    : isRussianToKazakh
    ? "Russian Cyrillic (Input)"
    : "English Latin (Input)";

  const outputLabel = isLatinToCyrillic
    ? "Kazakh Cyrillic (Output)"
    : isCyrillicToLatin
    ? "Latin Text (Output)"
    : isRussianToKazakh
    ? "Kazakh Cyrillic (Output)"
    : "Kazakh Latin (Output)";

  const inputPlaceholder = isLatinToCyrillic
    ? "Type in Latin script here..."
    : isCyrillicToLatin
    ? "Қазақша кириллицамен жазыңыз..."
    : isRussianToKazakh
    ? "Введите текст на русском..."
    : "Type in English alphabet...";

  const outputPlaceholder = isLatinToCyrillic
    ? "Kazakh Cyrillic text will appear here..."
    : isCyrillicToLatin
    ? "Latin text will appear here..."
    : isRussianToKazakh
    ? "Қазақша мәтін осында пайда болады..."
    : "Kazakh Latin text with diacritics will appear here...";

  return (
    <div className="mt-8 sm:mt-12 lg:mt-16 w-full text-left flex flex-col gap-4 sm:gap-6 lg:gap-8">
      {/* Direction Selector */}
      <div className="flex justify-center">
        <div className="inline-flex flex-wrap gap-2 bg-muted/50 rounded-lg p-1">
          <button
            onClick={() => {
              if (direction !== "latinToCyrillic") {
                setDirection("latinToCyrillic");
                // Clear text when switching modes
                setInputText("");
                setTransliteratedText("");
              }
            }}
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
              "touch-manipulation",
              isLatinToCyrillic
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            )}
            title="Convert Latin to Kazakh Cyrillic"
          >
            <span className="whitespace-nowrap">Latin → Cyrillic</span>
          </button>
          <button
            onClick={() => {
              if (direction !== "cyrillicToLatin") {
                setDirection("cyrillicToLatin");
                // Clear text when switching modes
                setInputText("");
                setTransliteratedText("");
              }
            }}
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
              "touch-manipulation",
              isCyrillicToLatin
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            )}
            title="Convert Kazakh Cyrillic to Latin"
          >
            <span className="whitespace-nowrap">Cyrillic → Latin</span>
          </button>
          <button
            onClick={() => {
              if (direction !== "russianToKazakh") {
                setDirection("russianToKazakh");
                // Clear text when switching modes
                setInputText("");
                setTransliteratedText("");
              }
            }}
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
              "touch-manipulation",
              isRussianToKazakh
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            )}
            title="Convert Russian Cyrillic to Kazakh Cyrillic"
          >
            <span className="whitespace-nowrap">Russian → Kazakh</span>
          </button>
          <button
            onClick={() => {
              if (direction !== "englishToKazakhLatin") {
                setDirection("englishToKazakhLatin");
                // Clear text when switching modes
                setInputText("");
                setTransliteratedText("");
              }
            }}
            className={cn(
              "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all",
              "touch-manipulation",
              isEnglishToKazakhLatin
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            )}
            title="Convert English Latin to Kazakh Latin with diacritics"
          >
            <span className="whitespace-nowrap">Latin → Qazaq Latin</span>
          </button>
        </div>
      </div>

      {/* Input/Output Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        {/* Input Section */}
        <div className="flex flex-col gap-2 sm:gap-4">
          <label htmlFor="input-text" className="text-base sm:text-lg font-semibold text-foreground">
            {inputLabel}
          </label>
          <textarea
            ref={textareaRef}
            id="input-text"
            value={inputText}
            onChange={handleInputChange}
            placeholder={inputPlaceholder}
            className={cn(
              "w-full min-h-[200px] sm:min-h-[300px] lg:min-h-[400px] bg-card",
              "border border-input rounded-xl p-3 sm:p-4 lg:p-6",
              "font-mono resize-y text-sm sm:text-base lg:text-lg",
              "placeholder:text-muted-foreground",
              "focus-visible:ring-4 focus-visible:ring-ring/20 focus-visible:outline-none",
              "transition-colors"
            )}
          />
          <VirtualKeyboard onKeyClick={handleVirtualKeyClick} direction={direction} />
        </div>

        {/* Output Section */}
        <div className="flex flex-col gap-2 sm:gap-4">
          <label htmlFor="output-text" className="text-base sm:text-lg font-semibold text-foreground">
            {outputLabel}
          </label>
          <div className="relative">
            <textarea
              id="output-text"
              value={transliteratedText}
              readOnly
              placeholder={outputPlaceholder}
              className={cn(
                "w-full min-h-[200px] sm:min-h-[300px] lg:min-h-[400px] bg-muted/50",
                "border border-input rounded-xl p-3 sm:p-4 lg:p-6",
                "font-mono resize-y text-sm sm:text-base lg:text-lg",
                "placeholder:text-muted-foreground",
                "text-foreground"
              )}
            />
            {/* Copy Button - Bottom Right Corner */}
            <button
              onClick={copyToClipboard}
              disabled={!transliteratedText}
              className={cn(
                "absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all",
                "bg-background/80 backdrop-blur-sm border border-border/50",
                "hover:bg-accent hover:text-accent-foreground",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "touch-manipulation shadow-sm",
                copied ? "text-green-600" : "text-muted-foreground"
              )}
              title="Copy to clipboard"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-4 sm:gap-6">
        <button
          onClick={clearText}
          className={cn(
            "px-4 sm:px-6 lg:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-base lg:text-lg",
            "bg-secondary text-secondary-foreground",
            "hover:bg-secondary/80",
            "transition-colors",
            "font-semibold"
          )}
        >
          Clear All
        </button>
      </div>

      {/* Info Section */}
      <div className="text-center text-sm sm:text-base text-muted-foreground max-w-4xl mx-auto hidden">
        <p className="mb-3 sm:mb-4 px-2">
          Type in {isLatinToCyrillic ? "Latin script" : isCyrillicToLatin ? "Kazakh Cyrillic" : isRussianToKazakh ? "Russian Cyrillic" : "English alphabet"} and see it automatically transliterated to {isLatinToCyrillic ? "Kazakh Cyrillic" : isCyrillicToLatin ? "Latin script with diacritics" : isRussianToKazakh ? "Kazakh Cyrillic" : "Kazakh Latin with diacritics"}
        </p>

        <div className="bg-muted/30 rounded-lg p-3 sm:p-4 lg:p-6 text-left">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-foreground">
            {isCyrillicToLatin ? "Cyrillic to Latin Mappings:" : isLatinToCyrillic ? "Latin to Cyrillic Mappings:" : isRussianToKazakh ? "Russian to Kazakh Conversion:" : "English to Kazakh Latin Mappings:"}
          </h3>

          {isCyrillicToLatin ? (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Category</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Cyrillic</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Latin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Kazakh-specific</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ә, ө, ү, ұ, қ, ғ, һ, і, ң</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ä, ö, ü, ū, q, ğ, h, ı, ñ</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Special Letters</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ч, ш, ж, й</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ch/Ç, ş/Ş, j/J, i'</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Standard Letters</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm break-all">а, б, в, г, д, е, з, и, к, л, м, н, о, п, р, с, т, у, ф, х, ц, ы</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm break-all">a, b, v, g, d, e, z, i, k, l, m, n, o, p, r, s, t, u, f, h, ts, y</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Russian Letters</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ё, ю, я</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">yo, yu, ya</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : isLatinToCyrillic ? (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Category</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Latin</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Cyrillic</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Basic Letters</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm break-all">a, b, c, d, e, f, g, h, i, j, k, l, m, n, o, p, q, r, s, t, u, v, w, x, y, z</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">а, б, ц, д, е, ф, г, х, и, ж, к, л, м, н, о, п, қ, р, с, т, ұ, в, у, х, ы, з</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Apostrophe-based</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">a', o', u', i', y', g'</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ә, ө, ү, й, і, ғ</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Special Combinations</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ch, sh, zh, ng, gh</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ч, ш, ж, ң, ғ</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : isRussianToKazakh ? (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Russian Letter</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Kazakh Equivalent</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ь, ъ</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">(removed)</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm">Soft/hard signs omitted</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">щ</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ш</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm">Approximated as 'sh'</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">э</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">е</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm">Standard conversion</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">Others</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">(unchanged)</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm">Shared letters remain as is</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Category</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">English Latin</th>
                    <th className="text-left py-1 sm:py-2 px-2 sm:px-3 font-semibold text-foreground">Kazakh Latin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Apostrophe notation</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">a', o', u', y', g'</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ä, ö, ü, ı, ğ</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Digraphs</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">sh, ch, ng, gh, zh</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ş, ç, ñ, ğ, j</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Macron notation</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">u-, uu</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">ū</td>
                  </tr>
                  <tr>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-medium text-foreground text-xs sm:text-sm">Standard letters</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm break-all">a-z (others)</td>
                    <td className="py-1 sm:py-2 px-2 sm:px-3 font-mono text-xs sm:text-sm">Unchanged (a, b, d, e, etc.)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p className="text-muted-foreground/80 italic text-xs sm:text-sm mt-3 sm:mt-4">
            {isRussianToKazakh
              ? "Russian to Kazakh conversion removes soft/hard signs (ь, ъ), converts щ → ш, and э → е. Common Cyrillic letters remain unchanged."
              : isCyrillicToLatin
              ? "Cyrillic to Latin uses modern diacritical marks (ä, ö, ü, ğ, ş, ñ, etc.) for accurate representation of Kazakh sounds."
              : isLatinToCyrillic
              ? "Latin to Cyrillic supports apostrophe notation (a', o', u') and digraphs (ch, sh, zh, ng) for Kazakh-specific letters."
              : "English to Kazakh Latin converts standard alphabet to Kazakh Latin with diacritics. Use apostrophes (a', o', u') or digraphs (sh, ch, ng) for Kazakh-specific sounds."}
          </p>
        </div>
      </div>
    </div>
  );
}
